import { useState, useEffect, useMemo } from 'react';

interface LiteModeState {
  /** Lite mode активен - отключаем тяжёлые эффекты */
  isLiteMode: boolean;
  /** Пользователь предпочитает reduced motion */
  prefersReducedMotion: boolean;
  /** Мобильное устройство */
  isMobile: boolean;
  /** Low-end устройство (мало памяти, слабый CPU) */
  isLowEnd: boolean;
  /** Telegram WebView */
  isTelegramWebView: boolean;
}

/**
 * Хук для определения необходимости "облегчённого" режима
 * Учитывает:
 * - prefers-reduced-motion
 * - Telegram WebView на мобильных
 * - Low-end устройства (hardwareConcurrency, deviceMemory)
 * - Размер экрана
 */
export function useLiteMode(): LiteModeState {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Определяем статические значения один раз
  const staticValues = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isLowEnd: false,
        isTelegramWebView: false,
      };
    }

    const isMobile = window.innerWidth < 768 || 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Проверяем Telegram WebView
    const isTelegramWebView = Boolean(
      (window as any).Telegram?.WebApp || 
      navigator.userAgent.includes('Telegram') ||
      window.location.href.includes('tgWebAppData')
    );

    // Определяем low-end устройство
    const nav = navigator as any;
    const hardwareConcurrency = nav.hardwareConcurrency || 4;
    const deviceMemory = nav.deviceMemory || 4; // GB
    
    // Low-end: <= 4 ядра или <= 4GB RAM
    const isLowEnd = hardwareConcurrency <= 4 || deviceMemory <= 4;

    return {
      isMobile,
      isLowEnd,
      isTelegramWebView,
    };
  }, []);

  // Слушаем изменения prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Lite mode активен если:
  // 1. Пользователь предпочитает reduced motion
  // 2. Telegram WebView (всегда, не только на мобильных - WebView имеет проблемы с backdrop-filter)
  // 3. Low-end устройство
  const isLiteMode = useMemo(() => {
    return prefersReducedMotion || 
           staticValues.isTelegramWebView ||
           (staticValues.isLowEnd && staticValues.isMobile);
  }, [prefersReducedMotion, staticValues]);

  return {
    isLiteMode,
    prefersReducedMotion,
    ...staticValues,
  };
}

/**
 * Контекст для глобального доступа к lite mode
 * Используется для избежания множественных вызовов хука
 */
import { createContext, useContext, ReactNode } from 'react';

const LiteModeContext = createContext<LiteModeState | null>(null);

export function LiteModeProvider({ children }: { children: ReactNode }) {
  const liteModeState = useLiteMode();
  
  return (
    <LiteModeContext.Provider value={liteModeState}>
      {children}
    </LiteModeContext.Provider>
  );
}

export function useLiteModeContext(): LiteModeState {
  const context = useContext(LiteModeContext);
  if (!context) {
    // Fallback если провайдер не установлен
    return {
      isLiteMode: false,
      prefersReducedMotion: false,
      isMobile: false,
      isLowEnd: false,
      isTelegramWebView: false,
    };
  }
  return context;
}

/**
 * Хелпер для определения, нужно ли анимировать
 * Используется во всех компонентах с тяжёлыми анимациями
 */
export function useShouldAnimate(): boolean {
  const { isLiteMode, prefersReducedMotion } = useLiteModeContext();
  return !isLiteMode && !prefersReducedMotion;
}

/**
 * Возвращает объект с настройками анимации для framer-motion
 * В lite mode возвращает пустые/минимальные анимации
 */
export function useAnimationConfig() {
  const shouldAnimate = useShouldAnimate();
  
  return {
    shouldAnimate,
    // Hover/tap анимации
    hoverScale: shouldAnimate ? { scale: 1.02 } : {},
    tapScale: shouldAnimate ? { scale: 0.98 } : {},
    // Page transitions
    pageTransition: shouldAnimate 
      ? { type: "spring", stiffness: 300, damping: 30 }
      : { duration: 0.15 },
    // Fade variants
    fadeVariants: shouldAnimate 
      ? {
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -10 },
        }
      : {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        },
    // Для AnimatePresence - в lite mode отключаем
    animatePresenceMode: shouldAnimate ? 'wait' as const : 'sync' as const,
  };
}

